/**
 * No one-letter word at the end of a line.
 *
 * Polish, Czech and Slovak are full of one-letter words - the prepositions and
 * conjunctions "i", "o", "u", "w", "z", "a", "k", "s", "v" - and a browser breaks a line
 * after them as happily as after any other word, leaving "w" at the end of one line and
 * its noun at the start of the next. Typographers tie them to the word that follows with a
 * no-break space (U+00A0).
 *
 * **CSS cannot do this.** `text-wrap: balance` evens out the lengths of a few lines and
 * `pretty` avoids a short last line; neither knows what a word *is*. `overflow-wrap` is for
 * a single word longer than its box. The only thing a browser will not break at is a no-break
 * space, so the space is what has to change.
 *
 * **Why it runs on the DOM and not in each component.** The text reaches the page through
 * dozens of components - rich text, headings, cards, hand-written sections - and every new
 * one would have to remember. `installTypography` watches `#root` and fixes each text node
 * as React writes it, inside the mutation-observer microtask, so the browser never paints
 * the unfixed text. React is not disturbed: it compares its own props, never the DOM's
 * text, and writes a node again only when the string it was given changes - at which point
 * the observer fixes that one again.
 *
 * What it leaves alone: a letter that is the end of a longer word (`Wi-Fi a`, `O'Neil x`),
 * a letter that ends a node nothing follows, and `code`, `pre`, `textarea`, `script`,
 * `style` and anything editable. Attribute text (`alt`, `aria-label`) is not a text node
 * and does not wrap.
 */

const NBSP = ' ';
const WORD_CHAR = /[\p{L}\p{N}_'’-]/u;
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE']);

/**
 * `text` with the space after every one-letter word made a no-break space.
 * `hasNext` says something follows this text in the same parent (an inline element), so a
 * one-letter word at its very end is still tied to it.
 */
export function tieSingleLetters(text: string, hasNext = false): string {
  if (text.length < 2) return text;

  return text.replace(/\p{L} (?=\S|$)/gu, (match, offset: number, whole: string) => {
    const previous = offset > 0 ? whole[offset - 1] : '';
    if (previous && WORD_CHAR.test(previous)) return match; // the tail of a longer word
    if (offset + match.length === whole.length && !hasNext) return match; // nothing to tie to
    return match.slice(0, -1) + NBSP;
  });
}

const isSkipped = (node: Node): boolean => {
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (SKIP_TAGS.has(el.tagName) || el.isContentEditable) return true;
  }
  return false;
};

function fixText(node: Text) {
  if (isSkipped(node)) return;
  const next = tieSingleLetters(node.data, node.nextSibling !== null);
  if (next !== node.data) node.data = next;
}

function fixTree(node: Node) {
  if (node.nodeType === Node.TEXT_NODE) {
    fixText(node as Text);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  if (SKIP_TAGS.has((node as Element).tagName)) return;

  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) fixText(n as Text);
}

/** Fix `root` now and keep fixing what is written into it. Returns a function that stops. */
export function installTypography(root: HTMLElement): () => void {
  fixTree(root);

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'characterData') fixText(record.target as Text);
      else record.addedNodes.forEach(fixTree);
    }
  });
  observer.observe(root, { childList: true, characterData: true, subtree: true });

  return () => observer.disconnect();
}
